import { Eye, EyeOff } from "lucide-react"
import React, { memo } from "react"

/**
 * Props for the PasswordEyeButton component.
 */
interface PasswordEyeButtonProps {
  /**
   * Boolean flag indicating whether the password is visible.
   */
  show: boolean

  /**
   * Function to toggle the password visibility.
   */
  onToggle: () => void

  /**
   * Unique identifier for the button element.
   */
  id: string
}

/**
 * PasswordEyeButton component renders an eye icon button to toggle password visibility.
 * It updates the icon and aria-label based on the current visibility state.
 *
 * @param {PasswordEyeButtonProps} props - The properties for the PasswordEyeButton component.
 * @returns {JSX.Element} The rendered password visibility toggle button.
 */
const PasswordEyeButton: React.FC<PasswordEyeButtonProps> = memo(
  ({ show, onToggle, id }) => (
    <button
      data-automation-id={id}
      id={id}
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 transition-colors"
      aria-label={show ? "Hide password" : "Show password"}
    >
      {show ? <EyeOff size={20} /> : <Eye size={20} />}
    </button>
  )
)

export default PasswordEyeButton
